import { useEffect, useState } from "react";
import { ShoppingBag, ExternalLink, Sparkles, Heart, CheckCircle2, ArrowRight, DollarSign, BarChart3, Zap, LayoutGrid, Tag, Globe, ShoppingCart } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import Brand, { GoliMark } from "./Brand";

/* =========================================================================
   GoliGummiesCard — Mike's flagship supplement promotion.

   Three stacked sections:
     1) HERO PRODUCT CARD — Promote Goli + Earn 20% commission
     2) GOLI FOR GOOD — emotional charity / brand-trust banner
     3) WHY BECOME A PARTNER — perks grid with Sign-Up CTA

   - Sales URL → buyer goes to Goli, Mike earns commission
   - Partner signup URL → member becomes a Goli partner under Mike;
     Mike earns 20% lifetime on their earnings.
========================================================================= */

const GOLI_BENEFITS = [
  { icon: Heart,        text: "Easy to swallow gummies — no more horse-pill struggles" },
  { icon: Sparkles,     text: "Vegan · Non-GMO · Gluten-free · Made with real fruit" },
  { icon: CheckCircle2, text: "Anyone can sign up — no recruiting downlines, no monthly minimums" },
  { icon: DollarSign,   text: "You earn 20% commission promoting it" },
];

const PARTNER_PERKS = [
  { icon: BarChart3,    title: "Real-time Stats",       text: "Track your performance instantly inside the Goli partner portal." },
  { icon: Zap,          title: "Instant Payouts",       text: "Get paid fast — no 30-day waits, no payout minimums." },
  { icon: LayoutGrid,   title: "One Portal, All Sales", text: "View Goli.com AND Amazon orders in one dashboard. Custom affiliate link + promo code." },
  { icon: Tag,          title: "Share & Earn",          text: "Share your link · earn 15% on every sale · plus 20% of earnings from anyone you refer." },
  { icon: Globe,        title: "Free Global Shipping",  text: "Goli ships to 200+ countries with taxes & duties included — earn on international traffic." },
  { icon: ShoppingCart, title: "High Average Order",    text: "+ AOV per order means bigger commissions every time someone buys." },
];

export default function GoliGummiesCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/goli/links").then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  const copyAffiliateLink = async () => {
    try {
      await navigator.clipboard.writeText(data.partner_signup_url);
      toast.success("Goli affiliate signup link copied — share it with anyone who wants to earn from gummies!");
    } catch {
      toast.error("Could not copy. Right-click the link and copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="goli-gummies-card">
      {/* ============== 1. HERO PRODUCT CARD ============== */}
      <div
        className="rounded-2xl border-2 border-[#F39A1F] overflow-hidden shadow-xl"
        style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 100%)" }}
      >
        <div className="grid md:grid-cols-[260px_1fr]">
          {/* IMAGE PANEL */}
          <div className="bg-black flex items-center justify-center p-4 relative overflow-hidden md:aspect-auto aspect-square">
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(201,162,39,0.18), transparent 60%)" }}/>
            <img src={data.image} alt="Goli Nutrition Gummies — full lineup" className="relative max-h-[260px] w-auto object-contain drop-shadow-2xl" data-testid="goli-image"/>
            <div className="absolute top-3 left-3 bg-[#22693C] text-white text-[10px] font-bold px-3 py-1 rounded-full tracking-[0.18em] shadow-md">★ 26% OFF</div>
          </div>

          {/* COPY + CTA PANEL */}
          <div className="p-6 sm:p-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F39A1F] font-bold flex items-center gap-1.5">★ <Brand/> · WELLNESS PARTNER ★</div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <GoliMark size="md"/>
              <p className="text-2xl md:text-3xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: "#0F172A" }}>
                World's First Apple Cider Vinegar Gummies
              </p>
            </div>
            <p className="text-foreground/80 mt-2 italic text-base">Mike's pick — <strong>actually easy to swallow</strong>. No more horse-pill struggles.</p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2" style={{ background: "#22693C", borderColor: "#22693C", color: "#FAF9F6" }}>
                <CheckCircle2 className="w-4 h-4"/>
                <span className="text-[11px] tracking-[0.18em] uppercase font-bold">NO QUOTAS · ANYONE CAN JOIN</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2" style={{ background: "#FFFFFF", borderColor: "#22693C", color: "#22693C" }}>
                <Globe className="w-4 h-4"/>
                <span className="text-[11px] tracking-[0.18em] uppercase font-bold">FREE SHIPPING · 200+ COUNTRIES</span>
              </div>
            </div>

            <ul className="mt-4 space-y-2">
              {GOLI_BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <li key={i} className="flex items-start gap-2.5">
                    <Icon className="w-4 h-4 text-[#22693C] mt-0.5 flex-shrink-0"/>
                    <span className="text-sm text-foreground">{b.text}</span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap gap-1.5 mt-4">
              {data.products.map((p) => (
                <span key={p} className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold border" style={{ background: "#FFFFFF", color: "#0F172A", borderColor: "#F39A1F" }}>{p}</span>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <a href={data.sales_url} target="_blank" rel="noopener noreferrer" data-testid="goli-buy-btn" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg font-bold uppercase tracking-[0.16em] text-sm transition shadow-md hover:shadow-lg" style={{ background: "#2E9F3A", color: "#FAF9F6" }}>
                <ShoppingBag className="w-4 h-4"/> Get 26% Off <ExternalLink className="w-3.5 h-3.5 opacity-70"/>
              </a>
              <a href={data.partner_signup_url} target="_blank" rel="noopener noreferrer" data-testid="goli-affiliate-signup-btn" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg font-bold uppercase tracking-[0.16em] text-sm transition border-2" style={{ background: "#FFFFFF", color: "#0F172A", borderColor: "#0F172A" }}>
                <DollarSign className="w-4 h-4"/> Earn 20% Commission <ArrowRight className="w-3.5 h-3.5"/>
              </a>
            </div>

            <div className="flex items-center gap-3 mt-3 text-[11px] text-foreground/60 flex-wrap">
              <button type="button" onClick={copyAffiliateLink} data-testid="goli-copy-affiliate-link-btn" className="underline hover:text-red-wine transition">
                Copy the affiliate signup link to share
              </button>
              <span>·</span>
              <span className="italic">Lifetime commission on every order</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============== 2. GOLI FOR GOOD CHARITY BANNER ============== */}
      <div
        data-testid="goli-for-good"
        className="rounded-2xl overflow-hidden shadow-xl border-2 border-[#F39A1F]"
        style={{ background: "linear-gradient(135deg, #0a0e2c 0%, #1a1f4a 100%)" }}
      >
        <div className="grid md:grid-cols-[1fr_280px]">
          <div className="p-6 sm:p-8 text-white">
            {/* Official Goli for Good logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/cikhtkbr_Screenshot%202026-05-07%20155353.png"
              alt="Goli for Good"
              className="h-16 w-auto rounded-md shadow-lg mb-3"
            />
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#FFC847] font-bold flex items-center gap-1.5">★ A <Brand light/> WELLNESS PARTNER ★</div>
            <h3 className="text-3xl md:text-4xl mt-2 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: "#FFC847" }}>
              Every Gummy You Sell Helps Children Worldwide.
            </h3>
            <p className="text-white/85 mt-3 leading-relaxed">
              <strong className="text-[#FFC847]">Goli for Good</strong> is Goli's initiative committed to playing a profound role in supporting the health of our planet and local communities across the globe.
            </p>
            <p className="text-white/75 mt-3 italic text-sm">
              When you share your link, you're not just earning commission — you're helping fund clean water, nutrition, and care for kids in 200+ countries. <span className="text-[#FFC847] font-bold not-italic">A faith-driven mission you can stand behind.</span>
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,200,71,0.15)", color: "#FFC847" }}>
              <Heart className="w-4 h-4"/>
              <span className="text-[11px] tracking-[0.18em] uppercase font-bold">Faith · Wellness · Generosity</span>
            </div>
          </div>
          {/* Charity image — smiling child basket */}
          <div className="relative bg-[#0a0e2c] min-h-[200px] md:min-h-0">
            <img
              src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/a7nyl45a_GOLI%20FOR%20GOOD%20CAMPAIGN.png"
              alt="Goli for Good — supporting children's health worldwide"
              className="w-full h-full object-contain p-2"
              style={{ objectPosition: "right center" }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(10,14,44,0.6) 0%, rgba(10,14,44,0) 30%)" }}/>
          </div>
        </div>
      </div>

      {/* ============== 3. WHY BECOME A PARTNER — clean Goli-style: white card, bright happy accents ============== */}
      <div
        data-testid="goli-partner-perks"
        className="rounded-2xl overflow-hidden shadow-xl bg-white border-2"
        style={{ borderColor: "#FFE5B4" }}
      >
        <div className="p-7 sm:p-9">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase font-bold mb-3"
                   style={{ background: "#D9EEFB", color: "#C0392B" }}>
                ★ Join The Partner Program ★
              </div>
              <h3 className="text-3xl md:text-4xl uppercase mt-1 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: "#0F172A" }}>
                Why become a Goli wellness partner?
              </h3>
              <p className="text-foreground/75 mt-2 max-w-2xl">
                It's <strong style={{ color: "#C0392B" }}>free to join</strong>, anyone can sign up, and you start earning real commissions the day you start sharing.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PARTNER_PERKS.map((p, i) => {
              const Icon = p.icon;
              /* Rotating Goli-style happy palette — one accent per card. */
              const colors = ["#C0392B", "#1976D2", "#2E9F3A", "#E91E63", "#F39A1F", "#0F172A"];
              const accent = colors[i % colors.length];
              return (
                <div key={i} className="rounded-xl p-5 bg-white border-2 hover:shadow-lg transition" style={{ borderColor: `${accent}33` }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center mb-3" style={{ background: `${accent}1A` }}>
                    <Icon className="w-5 h-5" style={{ color: accent }}/>
                  </div>
                  <div className="font-bold text-base" style={{ color: "#0F172A" }}>{p.title}</div>
                  <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4 justify-between">
            <div className="text-sm text-foreground/75">
              <strong style={{ color: "#C0392B" }}>Mike's referral is baked into the link.</strong> Tap and create your free Goli partner account in 2 minutes.
            </div>
            <a
              href={data.partner_signup_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="goli-partner-signup-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-[0.18em] text-sm transition shadow-md hover:shadow-xl"
              style={{ background: "#C0392B", color: "white" }}
            >
              Start Free Trial <ArrowRight className="w-4 h-4"/>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
